import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2, BookOpen, Package } from 'lucide-react';
import api from '../../services/api';

let stripePromise = null;
function getStripePromise() {
    if (!stripePromise) {
        stripePromise = api.get('/stripe/config.php').then(({ data }) => loadStripe(data.data.public_key));
    }
    return stripePromise;
}

export const fmtEuro = (p) => (Number.isInteger(p) ? String(p) : Number(p).toFixed(2).replace('.', ',')) + ' €';

/**
 * Paiement d'une notion à l'unité ou d'un pack de notions (migration 186) : récapitulatif, puis Stripe Checkout
 * embarqué (api/notions/buy.php). Le webhook ouvre la notion ou crédite le pack ; le retour se fait sur l'onglet
 * Notions de la thématique (?notions_achat=ok ou ?notions_pack=ok). Hors production, « Simuler » enregistre sans Stripe.
 * Props : item = { kind: 'notion', id, titre, prix } | { kind: 'pack', offer: { id, label, price, notions }, unitMin, unitMax },
 *         scopeSlug, onClose, onSimulated.
 */
/** Économie minimale garantie d'un pack (en %, arrondie à l'inférieur) : prix du pack face au même nombre de notions
 *  au prix le plus bas. 0 si le pack ne fait rien gagner dans le pire des cas. */
export const packMinSaving = (p, unitMin) => {
    const full = (p?.notions || 0) * (unitMin || 0);
    // Arrondi aux centièmes d'abord : 1 - 24/30 vaut 0,1999… en virgule flottante (19 % au lieu de 20 %).
    return full > p.price ? Math.floor(Math.round((1 - p.price / full) * 10000) / 100) : 0;
};

export default function NotionCheckoutModal({ item, scopeSlug, onClose, onSimulated }) {
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const isDev = import.meta.env.DEV || new URLSearchParams(window.location.search).has('simulate');
    const isPack = item.kind === 'pack';
    const total = isPack ? item.offer.price : item.prix;
    // Les notions ont des prix différents : seule l'économie minimale (sur les moins chères) est garantie.
    const range = isPack && item.unitMin > 0
        ? (item.unitMax > item.unitMin ? `de ${fmtEuro(item.unitMin)} à ${fmtEuro(item.unitMax)}` : fmtEuro(item.unitMin)) : '';

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    }, [onClose]);

    const pay = async (simulate = false) => {
        setError(null); setLoading(true);
        try {
            const body = isPack ? { pack_offer_id: item.offer.id } : { notion_id: item.id };
            const { data } = await api.post('/notions/buy.php', { ...body, scope_slug: scopeSlug, simulate });
            if (data.data?.simulated) { onSimulated?.(); onClose(); return; }
            if (data.data?.client_secret) setClientSecret(data.data.client_secret);
        } catch (e) { setError(e.response?.data?.message || 'Erreur'); }
        setLoading(false);
    };

    return (
        <div className="checkout-modal-overlay" onClick={onClose}>
            <div className="checkout-modal checkout-modal--wide" onClick={e => e.stopPropagation()}>
                <div className="checkout-modal__header">
                    <span className="checkout-modal__title">
                        {clientSecret ? `Paiement — ${isPack ? item.offer.label : 'Notion'} (${fmtEuro(total)})`
                            : isPack ? <><Package size={18} /> {item.offer.label}</> : <><BookOpen size={18} /> Acheter une notion</>}
                    </span>
                    <button className="checkout-modal__close" onClick={onClose} aria-label="Fermer"><X size={20} /></button>
                </div>
                <div className="checkout-modal__body">
                    {error && <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>}

                    {!clientSecret && (
                        <div className="sub-modal-plans">
                            {/* Ticket : ce qu'on achète, le total, payer (pas d'arguments commerciaux à ce stade). */}
                            <div className="nlib-ticket">
                                <div className="nlib-ticket__line">
                                    <span>
                                        <strong>{isPack ? item.offer.label : item.titre}</strong>
                                        <em>{isPack
                                            ? `soit ${fmtEuro(Math.round(total / item.offer.notions * 100) / 100)} la notion · à choisir quand tu veux, quel que soit leur prix${range ? ` (${range} à l'unité)` : ''}`
                                            : 'Fiche complète et 20 questions · accès illimité'}</em>
                                    </span>
                                    <strong>{fmtEuro(total)}</strong>
                                </div>
                                <div className="nlib-ticket__total"><span>Total TTC</span><strong>{fmtEuro(total)}</strong></div>
                            </div>
                            <button className="btn nlib-btn sub-modal-cta" onClick={() => pay(false)} disabled={loading}>
                                {loading ? <><Loader2 size={16} className="spin" /> Chargement…</> : <><CreditCard size={16} /> Payer {fmtEuro(total)}</>}
                            </button>
                            {isDev && (
                                <button className="btn btn-sm" onClick={() => pay(true)}
                                    style={{ display: 'block', margin: '8px auto 0', fontSize: '0.75em', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
                                    Simuler (dev)
                                </button>
                            )}
                            <p className="sub-modal-note">Paiement unique, facture envoyée par Stripe. {isPack
                                ? 'Après le paiement, ouvre une notion et touche « Choisir cette notion ».'
                                : 'Un achat donne un accès illimité à la notion : fiche complète et 20 questions.'}</p>
                        </div>
                    )}

                    {clientSecret && (
                        <div>
                            <button className="sub-modal-back" onClick={() => { setClientSecret(null); setError(null); }}>← Retour</button>
                            <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
